import Immutable from 'immutable';
import { parseRawDate } from '../../../data/dateutils';
import { getSrchTokens, matchSrchTokens } from '../../../data/localsearch';
import { isEmpty } from '../../../data/validation';

let timestamp = 0;
const EMPTY_LIST = Immutable.List([]);

function getRowIndexes(rows, defaultFilter) {
	rows = rows || EMPTY_LIST;
	defaultFilter = defaultFilter || alwaysTrue;
	return rows.map((row, index) => row.get('_deleted') || !defaultFilter(row) ? -1 : index).filter(index => index >= 0).toArray();
}

export function createTabularInputIndex(ctx, rows, options) {
	//console.log('createTabularIndex', options);
	options = options || {};
	rows = rows || EMPTY_LIST;
	let rowIndex = getRowIndexes(rows, options.defaultFilter);
	if (options.withNewRow) {
		rowIndex.push(rows.size);
	}
	return Immutable.Map({
		rowIndex,
		withNewRow: options.withNewRow,
		sortIndexes: [],
		defaultFilter: options.defaultFilter,
		filterIndexes: [],
		filter: Immutable.List([]),
		pager: Immutable.Map({
			firstRow: 1,
			maxRowsPerPage: options.maxRowsPerPage || 14,
			totalCount: rowIndex.length
		})
	});
}

function getRangeFilterFunc(filter) {
	if (filter.get('valid')) {
		const from = filter.get('from');
		const to = filter.get('to');
		if (from === null || from === undefined) {
			if (to === null || to === undefined) {
				return alwaysTrue;
			} else {
				return function(value) {
					return value <= to;
				};
			}
		} else if (to === null || to === undefined) {
			return function(value) {
				return value >= from;
			};
		} else {
			return function(value) {
				return value >= from && value <= to;
			};
		}
	} else {
		return alwaysTrue;
	}
}

function getTextLikeFilterFunc(filter) {
	const srchTokens = getSrchTokens(filter);
	return function(value) {
		return matchSrchTokens(srchTokens, value);
	}
}

function getEqualFilterFunc(filter) {
	return function(value) {
		return value === filter;
	};
}

function alwaysTrue() {
	return true;
}

function getFilterFunc(type, filter) {
	if (type === 'lookup') {
		return getEqualFilterFunc(filter);
	} else if (type === 'text') {
		return getTextLikeFilterFunc(filter);
	} else if (type === 'searchNumber' || type === 'searchDate' || type === 'searchRuntime') {
		return getRangeFilterFunc(filter);
	} else {
		return alwaysTrue;
	}
}

export function setFilterValue(index, itemIndex, value) {
	if (isEmpty(value) && isEmpty(index.getIn(['filter', itemIndex]))) {
		return index;
	}
	return index.setIn(['filter', itemIndex], value);
}

function setRowIndex(index, rowIndex) {
	index = index.set('rowIndex', rowIndex);
	let pager = index.get('pager');
	let firstRow = pager.get('firstRow');
	if (firstRow >= rowIndex.length) {
		const maxRowsPerPage = pager.get('maxRowsPerPage');
		pager = pager.set('firstRow', Math.floor((rowIndex.length - 1) / maxRowsPerPage) * maxRowsPerPage + 1);
	} else if (firstRow < 1) {
		pager = pager.set('firstRow', 1);
	}
	pager = pager.set('totalCount', rowIndex.length);
	return index.set('pager', pager);
}

function defaultCompare(a, b) {
	if (a === null || a === undefined) {
		if (b === null || b === undefined) {
			return 0;
		}
		return -1;
	}
	if (b === null || b === undefined) {
		return 1;
	}
	if (a < b) {
		return -1;
	} else if (a > b) {
		return 1;
	}
	return 0;
}

function defaultCompareNullsLast(a, b) {
	if (a === null || a === undefined) {
		if (b === null || b === undefined) {
			return 0;
		}
		return 1;
	}
	if (b === null || b === undefined) {
		return -1;
	}
	if (a < b) {
		return -1;
	} else if (a > b) {
		return 1;
	}
	return 0;
}

export function toggleFilterVisible(index) {
	return index.set('filterVisible', !index.get('filterVisible'));
}

export function clearFilter(index) {
	return index.set('filter', Immutable.List([]));
}

export function updateDefaultFilter(index, defaultFilter) {
	return index.set('defaultFilter', defaultFilter);
}

export async function applyFilter(ctx, index, formItems, rows) {
	const filter = index.get('filter');
	rows = rows || EMPTY_LIST;
	let filters = [];
	let rowIndexes;
	if (index.get('filterVisible')) {
		for (let itemIndex = 0; itemIndex < formItems.size; itemIndex++) {
			let formItem = formItems.get(itemIndex);
			if (!formItem.getFilterType) {
				continue;
			}
			const filterValue = filter.get(itemIndex);
			if (isEmpty(filterValue)) {
				continue;
			}
			let path = formItem.getPath();
			let pathStr = typeof path === 'string' ? path : null;
			let filterEntry = {
				func: getFilterFunc(formItem.getFilterType(), filterValue),
				path,
				pathStr
			};
			if (formItem.getFilterType() === 'text') {
				const filterIndex = await getFilterIndex(ctx, index, formItem, itemIndex, rows);
				if (filterIndex) {
					filterEntry.values = filterIndex.values;
				}
			}
			filters.push(filterEntry);
		}
	}
	const defaultFilter = index.get('defaultFilter') || alwaysTrue;
	if (filters.length === 0) {
		rowIndexes = getRowIndexes(rows, defaultFilter);
	} else {
		rowIndexes = [];
		rows.forEach((row, index) => {
			if (row.get('_deleted')) {
				return;
			}
			if (!defaultFilter(row)) {
				return true;
			}
			if (!filters.some(filter => {
				if (filter.values) {
					return !filter.func(filter.values[index]);
				} else if (filter.pathStr) {
					return !filter.func(row.get(filter.pathStr));
				} else {
					return !filter.func(row.getIn(filter.path));
				}
			})) {
				rowIndexes.push(index);
			}
		})
	}
	if (index.get('withNewRow')) {
		rowIndexes.push(rows.size);
	}
	return refreshOrderBy(setRowIndex(index, rowIndexes), rows);
}

export async function getFilterIndex(ctx, index, formItem, itemIndex, rows) {
	if (formItem.getFilterType() !== 'text') {
		return null;
	}
	rows = rows || EMPTY_LIST;
	let filterIndexes = index.get('filterIndexes');
	let filterIndex = filterIndexes[itemIndex];
	if (!filterIndex || filterIndex.rows !== rows) {
		let values;
		const itemPath = formItem.getPath();

		if (Array.isArray(itemPath)) {
			values = rows.map(row => row.getIn(itemPath)).toArray();
		} else {
			values = rows.map(row => row.get(itemPath)).toArray();
		}

		if (formItem.getType() === 'lookup') {
			const lookup = formItem.getIndexLookup(ctx);
			const textMap = await lookup.getMultiText(values);
			values = values.map(value => getSrchTokens(textMap[value]).join(''))
		} else {
			values = values.map(value => getSrchTokens(value).join(''));
		}
		filterIndex = {
			rows,
			values
		};
		filterIndexes[itemIndex] = filterIndex;
	}
	return filterIndex;
}

export async function getSortIndex(ctx, index, formItem, itemIndex, rows) {
	const name = formItem.getName();
	rows = rows || EMPTY_LIST;
	let sortIndexes = index.get('sortIndexes');
	let sortIndex = sortIndexes[itemIndex];
	if (!sortIndex || sortIndex.rows !== rows) {
		const type = formItem.getType();
		let compare = formItem.getSortNullsLast() ? defaultCompareNullsLast : defaultCompare;
		const itemPath = formItem.getPath();
		let values;
		if (Array.isArray(itemPath)) {
			values = rows.map(row => row.getIn(itemPath)).toArray();
		} else {
			values = rows.map(row => row.get(itemPath)).toArray();
		}
		if (type === 'lookup') {
			const lookup = formItem.getIndexLookup(ctx);
			if (lookup) {
				const textMap = await lookup.getMultiText(values);
				values = values.map(value => textMap[value]);
				compare = Intl.Collator('cs').compare;
			}
		} else if (type === 'date') {
			values = values.map(value => parseRawDate(value));
		} else if (type === 'text') {
			compare = Intl.Collator('cs').compare;
		}
		sortIndex = {
			rows,
			compare,
			values
		};
		sortIndexes[itemIndex] = sortIndex;
	}
	return sortIndex;
}


export async function orderBy(index, sortIndex, newRowIndex, desc) {
	const compare = sortIndex.compare;
	const values = sortIndex.values;
	index.get('rowIndex').sort((idx1, idx2) => {
		if (idx1 === newRowIndex) {
			return 1;
		} else if (idx2 === newRowIndex) {
			return -1;
		}
		if (desc) {
			return -compare(values[idx1], values[idx2]);
		} else {
			return compare(values[idx1], values[idx2]);
		}
	});
	let currSortParams = Immutable.Map({
		sortIndex,
		newRowIndex,
		desc
	});
	return index.set('timestamp', timestamp++).set('currSortParams', currSortParams);
}

export async function refreshOrderBy(index, rows) {
	const csp = index.get('currSortParams');
	if (!csp) {
		return index;
	}
	return orderBy(index, csp.get('sortIndex'), rows.size, csp.get('desc'));
}

export function tabularIndexHasNextPage(index) {
	const rowIndex = index.get('rowIndex');
	const pager = index.get('pager');
	return (pager.get('firstRow') + pager.get('maxRowsPerPage')) <= rowIndex.length;
}

export function goToNextIndexPage(index) {
	let pager = index.get('pager');
	pager = pager.set('firstRow', pager.get('firstRow') + pager.get('maxRowsPerPage'));
	return index.set('pager', pager);
}


export function deleteRowFromIndex(index, rowIndex) {
	index = setRowIndex(index, index.get('rowIndex').filter(item => item !== rowIndex));
	return index.set('timestamp', timestamp++);
}

export function addRowToIndex(index, rowIndex) {
	let ri = index.get('rowIndex');
	ri.push(rowIndex);
	index = setRowIndex(index, ri);
	return index.set('timestamp', timestamp++);
}

const EMPTY_INDEX = Immutable.Map({
	pager: Immutable.Map({
		firstRow: 1,
		maxRowsPerPage: 10,
		totalCount: 0
	})

});

export function getEmptyIndex() {
	return EMPTY_INDEX.set('rowIndex', []);
}
